import { useEffect, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DynamicIcon from "../DynamicIcon";
import { selectSelectedMaintenance, selectSelectedMaintenanceLoading, deleteMaintenance, selectDeleteMaintenanceLoading, selectDeleteMaintenanceError, resetDeleteStatus } from "../../redux/maintenances/maintenancesSlice";
import { closeModal, setHasToRefresh, setModal } from "../../redux/rightModalSlice";
import LoadingSection from "../LoadingSection";
import moment from "moment";
import 'moment/locale/it'; // Importa la localizzazione italiana
import { selectAccountData } from "../../redux/main/accountSlice";
import { useColorPalette } from "../../hooks/useColorPalette";
import toast from "../../raadmin-template/Services/toast";
import JobCard from "../systemsPage/JobCard";
import AppointmentCard from "../AppointmentCard";

// Import del componente Folder per le tab
import Folder from "../templates/folder/Folder";

// Componente locale per visualizzare una coppia etichetta-valore in stile professionale
const DetailItem = ({ label, value, iconClass }) => (
  <div className="flex items-start gap-2 py-1">
    {iconClass && <DynamicIcon iconClass={iconClass + " text-blue-400 mt-1"} />}
    <div>
      <div className="text-xs text-gray-500 font-semibold leading-tight">{label}</div>
      <div className="text-base text-gray-800 break-words leading-snug">{value ?? <span className="text-gray-300">N/A</span>}</div>
    </div>
  </div>
);

export default function ViewMaintenance() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const selectedMaintenance = useSelector(selectSelectedMaintenance);
    const loading = useSelector(selectSelectedMaintenanceLoading);
    const deleteLoading = useSelector(selectDeleteMaintenanceLoading);
    const deleteError = useSelector(selectDeleteMaintenanceError);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [linkedJobs, setLinkedJobs] = useState(null); // commesse collegate segnalate dal backend
    const [selectedTab, setSelectedTab] = useState(0);
    // Commesse e appuntamenti collegati alla manutenzione (null = non ancora caricati)
    const [jobs, setJobs] = useState(null);
    const [jobsError, setJobsError] = useState(null);
    const [appointments, setAppointments] = useState(null);
    const [appointmentsError, setAppointmentsError] = useState(null);
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
    const token = localStorage.getItem('installamelo-auth-token');
    const accountData = useSelector(selectAccountData);
    const palette = useColorPalette();
    
    // Imposta la localizzazione di moment
    moment.locale('it');
    
    // Funzione per ottenere il colore dello stato delle attività di manutenzione
    const getMaintenanceActivityStatusColor = (statusId) => {
        if (!statusId || !accountData || !accountData.settings || !accountData.settings.maintenanceActivitiesStatus) return '#6c757d'; // Default: grigio
        
        const matchingStatus = accountData.settings.maintenanceActivitiesStatus.find(status => status._id === statusId);
        
        if (matchingStatus && matchingStatus.color) {
            return matchingStatus.color;
        }
        
        return '#6c757d'; // Default: grigio
    };
    
    // Array di titoli per le tab, con il numero di commesse e appuntamenti
    const tabTitles = [
        "Azioni",
        `Commesse${jobs && jobs.length > 0 ? ` (${jobs.length})` : ""}`,
        `Appuntamenti${appointments && appointments.length > 0 ? ` (${appointments.length})` : ""}`
    ];

    // Carico commesse (Job.maintenanceId) e appuntamenti (Appointment.maintenanceId) della manutenzione
    const maintenanceId = selectedMaintenance?._id;
    useEffect(() => {
        if (!maintenanceId || !accountData?._id || !token) return;
        setJobs(null);
        setJobsError(null);
        setAppointments(null);
        setAppointmentsError(null);
        const headers = { 'auth-token': token };
        axios.get(`${apiBaseUrl}/jobs/list`, { headers, params: { accountId: accountData._id, maintenanceId, length: 1000 } })
            .then(res => setJobs(res.data.data || []))
            .catch(() => { setJobs([]); setJobsError("Errore nel recupero delle commesse"); });
        axios.get(`${apiBaseUrl}/appointments/byMaintenanceId`, { headers, params: { accountId: accountData._id, maintenanceId } })
            .then(res => setAppointments(res.data.data || []))
            .catch(() => { setAppointments([]); setAppointmentsError("Errore nel recupero degli appuntamenti"); });
    }, [maintenanceId, accountData?._id, token, apiBaseUrl]);

    // Handle edit button click - navigates to the wizard in edit mode
    const handleEditClick = () => {
        if (selectedMaintenance && selectedMaintenance._id) {
            // Chiude la modale corrente
            dispatch(closeModal());
            
            // Naviga al wizard in modalità modifica
            navigate(`/maintenance/edit/${selectedMaintenance._id}`);
        }
    };
    
    // Handle delete maintenance
    const handleDeleteClick = () => {
        setLinkedJobs(null);
        setShowDeleteConfirm(true);
    };

    // Con force=false il backend puo' rispondere richiedendo conferma se la
    // manutenzione ha commesse collegate.
    const confirmDelete = (force = false) => {
        if (selectedMaintenance?._id) {
            dispatch(deleteMaintenance({
                maintenanceId: selectedMaintenance._id,
                token,
                force
            })).then((result) => {
                if (!result.error) {
                    setShowDeleteConfirm(false);
                    setLinkedJobs(null);
                    // Chiudo il modal e aggiorno la tabella
                    dispatch(closeModal());
                    dispatch(setHasToRefresh());
                    // Mostro notifica di successo
                    toast.success("Manutenzione eliminata con successo");
                } else if (result.payload && result.payload.requiresConfirmation) {
                    setLinkedJobs(result.payload.linkedJobs || []);
                }
            });
        }
    };
    
    // Reset error states when component unmounts
    useEffect(() => {
        return () => {
            dispatch(resetDeleteStatus());
        };
    }, [dispatch]);
    
    if (loading) {
        return <LoadingSection />;
    }
    
    if (!selectedMaintenance) {
        return (
            <div className="p-6">
                <h2 className="text-xl font-bold mb-4">Manutenzione non trovata</h2>
                <p className="text-gray-600">La manutenzione richiesta non è disponibile.</p>
                <div className="mt-4">
                    <button className="secondary" onClick={() => dispatch(closeModal())}>
                        <DynamicIcon iconClass="fas fa-arrow-left" />
                        Torna indietro
                    </button>
                </div>
            </div>
        );
    }
    
    // Contenuto delle tab
    const tabContents = [
        // Tab Azioni
        <div key="tab-azioni">
            <p className="text-sm mb-4" style={{ color: palette.textSubtle }}>Elenco delle azioni programmate per questa manutenzione.</p>
            {selectedMaintenance.actions && selectedMaintenance.actions.length > 0 ? (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titolo</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scadenza</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Note</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {selectedMaintenance.actions.map((action, index) => (
                                <tr key={action._id || index}>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="text-sm font-medium">{action.title}</div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="text-sm">
                                            {action.month && action.year ? (() => {
                                                // Formato MM/YYYY con zero iniziale per mesi a una cifra
                                                const monthNumber = action.month < 10 ? `0${action.month}` : `${action.month}`;
                                                return `${monthNumber}/${action.year}`;
                                            })() : '-'}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        {action.statusId ? (
                                            <span
                                                className="px-2 py-1 text-xs rounded-full"
                                                style={{ backgroundColor: getMaintenanceActivityStatusColor(action.statusId), color: '#fff' }}>
                                                {action.statusLabel}
                                            </span>
                                        ) : '-'}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="text-sm text-gray-500">{action.notes || '-'}</div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <DynamicIcon iconClass="fa-solid fa-clipboard-list text-4xl mb-2" style={{ color: palette.textSubtle }} />
                    <p className="text-sm" style={{ color: palette.textSubtle }}>Nessuna azione programmata</p>
                </div>
            )}
        </div>,
        // Tab Commesse
        <div key="tab-commesse">
            <p className="text-sm mb-4" style={{ color: palette.textSubtle }}>Commesse associate a questa manutenzione, con i loro appuntamenti.</p>
            {jobs === null ? (
                <LoadingSection isFixed={false} />
            ) : jobsError ? (
                <p className="text-sm text-red-500">{jobsError}</p>
            ) : jobs.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {jobs.map(job => (
                        <JobCard
                            key={job._id}
                            job={job}
                            accountId={accountData?._id}
                            token={token}
                            apiBaseUrl={apiBaseUrl}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <DynamicIcon iconClass="fa-solid fa-folder-open text-4xl mb-2" style={{ color: palette.textSubtle }} />
                    <p className="text-sm" style={{ color: palette.textSubtle }}>Nessuna commessa associata</p>
                </div>
            )}
        </div>,
        // Tab Appuntamenti
        <div key="tab-appuntamenti">
            <p className="text-sm mb-4" style={{ color: palette.textSubtle }}>Appuntamenti di questa manutenzione, dal più recente.</p>
            {appointments === null ? (
                <LoadingSection isFixed={false} />
            ) : appointmentsError ? (
                <p className="text-sm text-red-500">{appointmentsError}</p>
            ) : appointments.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {[...appointments]
                        .sort((a, b) => new Date(b.fromDate || 0) - new Date(a.fromDate || 0))
                        .map(appointment => (
                            <AppointmentCard
                                key={appointment._id}
                                appointment={appointment}
                                token={token}
                                apiBaseUrl={apiBaseUrl}
                            />
                        ))}
                </div>
            ) : (
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <DynamicIcon iconClass="fa-solid fa-calendar-alt text-4xl mb-2" style={{ color: palette.textSubtle }} />
                    <p className="text-sm" style={{ color: palette.textSubtle }}>Nessun appuntamento programmato</p>
                </div>
            )}
        </div>
    ];

    return (
        <div className="p-6">
            {deleteLoading && <LoadingSection isFixed />}
            
            {/* Layout a due colonne: sinistra (più stretta) e destra (più larga) */}
            <div className="flex flex-col xl:flex-row gap-6 mb-6">
                {/* Colonna sinistra (1/3) - Riepilogo dati */}
                <div className="w-full xl:w-1/3">
                    <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-100 mb-4" style={{ borderTop: `4px solid ${palette.primary}` }}>
                        {/* Header della card con sfondo verde delle tab */}
                        <div className="p-4" style={{ backgroundColor: palette.tableHeaderBg || '#629572', borderBottom: '1px solid #eee' }}>
                            <h3 className="text-lg font-bold flex items-center" style={{ color: palette.tableHeaderText || '#fff' }}>
                                <DynamicIcon iconClass="fa-solid fa-clipboard-check mr-3" />
                                Dati Manutenzione
                            </h3>
                        </div>
                        {/* Contenuto della card */}
                        <div className="p-5 space-y-3 bg-white">
                            <DetailItem label="Codice" value={selectedMaintenance.code} iconClass="fa-solid fa-barcode" />
                            <div className="border-b border-gray-100 my-2"></div>
                            <DetailItem label="Data creazione" value={selectedMaintenance.creationDate ? moment(selectedMaintenance.creationDate).format('DD/MM/YYYY HH:mm') : undefined} iconClass="fa-regular fa-calendar-plus" />
                            <div className="border-b border-gray-100 my-2"></div>
                            <DetailItem label="Cliente" value={selectedMaintenance.customerLabel} iconClass="fa-solid fa-user" />
                            <div className="border-b border-gray-100 my-2"></div>
                            <DetailItem label="Impianto" value={`${selectedMaintenance.systemLabel}${selectedMaintenance.systemTypes && selectedMaintenance.systemTypes.length > 0 ? ' (' + selectedMaintenance.systemTypes.map(type => type.systemTypeLabel || type.label || '').join(', ') + ')' : ''}`} iconClass="fa-solid fa-industry" />
                            <div className="border-b border-gray-100 my-2"></div>
                            <DetailItem label="Indirizzo" value={selectedMaintenance.systemAddressLabel} iconClass="fa-solid fa-location-dot" />
                            <div className="border-b border-gray-100 my-2"></div>
                            <DetailItem label="Distanza" value={selectedMaintenance.geoData ? `${Math.round(selectedMaintenance.geoData.distanceKm)} km - ${selectedMaintenance.geoData.distanceTime} min` : undefined} iconClass="fa-solid fa-route" />
                            <div className="border-b border-gray-100 my-2"></div>
                            <DetailItem label="Inizio Contratto" value={selectedMaintenance.contractStartDate ? moment(selectedMaintenance.contractStartDate).format('DD/MM/YYYY') : undefined} iconClass="fa-solid fa-calendar-check" />
                            <div className="border-b border-gray-100 my-2"></div>
                            <DetailItem label="Fine Contratto" value={selectedMaintenance.contractEndDate ? moment(selectedMaintenance.contractEndDate).format('DD/MM/YYYY') : undefined} iconClass="fa-solid fa-calendar-xmark" />
                            <div className="border-b border-gray-100 my-2"></div>
                            {selectedMaintenance.contractRenewal?.status && (
                                <>
                                    <div className="flex items-start gap-2 py-1">
                                        <DynamicIcon iconClass="fa-solid fa-file-signature text-green-500 mt-1" />
                                        <div>
                                            <div className="text-xs text-gray-500 font-semibold leading-tight">Rinnovo Contratto</div>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                                    selectedMaintenance.contractRenewal.status === 'rinnovato'
                                                        ? 'bg-green-100 text-green-700'
                                                        : selectedMaintenance.contractRenewal.status === 'non_rinnovato'
                                                        ? 'bg-red-100 text-red-700'
                                                        : 'bg-gray-100 text-gray-700'
                                                }`}>
                                                    {selectedMaintenance.contractRenewal.status === 'rinnovato'
                                                        ? 'Rinnovato'
                                                        : selectedMaintenance.contractRenewal.status === 'non_rinnovato'
                                                        ? 'Non rinnovato'
                                                        : 'Gestito'}
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-400 mt-1">
                                                {selectedMaintenance.contractRenewal.handledDate ? `il ${moment(selectedMaintenance.contractRenewal.handledDate).format('DD/MM/YYYY')}` : ''}
                                                {selectedMaintenance.contractRenewal.handledByLabel ? ` da ${selectedMaintenance.contractRenewal.handledByLabel}` : ''}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="border-b border-gray-100 my-2"></div>
                                </>
                            )}
                            {selectedMaintenance.contractFile?.urlFile && (
                                <>
                                    <div className="flex items-start gap-2 py-1">
                                        <DynamicIcon iconClass="fa-solid fa-file-contract text-blue-400 mt-1" />
                                        <div>
                                            <div className="text-xs text-gray-500 font-semibold leading-tight">File Contratto</div>
                                            <a 
                                                href={selectedMaintenance.contractFile.urlFile} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline text-base"
                                            >
                                                {selectedMaintenance.contractFile.fileName || 'Visualizza contratto'}
                                            </a>
                                        </div>
                                    </div>
                                    <div className="border-b border-gray-100 my-2"></div>
                                </>
                            )}
                            <DetailItem label="Note" value={selectedMaintenance.notes} iconClass="fa-solid fa-comment" />
                        </div>
                        
                        {/* Sezione bottoni */}
                        <div className="flex justify-end space-x-2 mt-4 p-4" style={{ backgroundColor: palette.footerBg || '#f8f9fa', borderTop: '1px solid #eee' }}>
                            <button
                                onClick={() => dispatch(closeModal())}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 py-2.5 px-4 rounded-md font-medium transition-colors duration-150 ease-in-out"
                                style={{
                                    backgroundColor: '#f0f0f0',
                                    color: '#444444',
                                    border: '1px solid #dddddd'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#e0e0e0'; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                            >
                                Chiudi
                            </button>
                            <button 
                                onClick={handleDeleteClick}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 py-2.5 px-4 rounded-md font-medium transition-colors duration-150 ease-in-out"
                                style={{
                                    backgroundColor: '#ffebee',
                                    color: '#d32f2f',
                                    border: '1px solid #ffcdd2'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#ffcdd2'; e.currentTarget.style.color = '#c62828'; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#ffebee'; e.currentTarget.style.color = '#d32f2f'; }}
                            >
                                <DynamicIcon iconClass="fas fa-trash" /> Elimina
                            </button>
                            <button 
                                onClick={handleEditClick} 
                                className="w-full sm:w-auto flex items-center justify-center gap-2 py-2.5 px-4 rounded-md font-medium transition-colors duration-150 ease-in-out"
                                style={{
                                    backgroundColor: palette.primary, 
                                    color: palette.textOnPrimary,
                                    border: `1px solid ${palette.primaryHover}`
                                }}
                                onMouseEnter={e => { e.currentTarget.style.backgroundColor = palette.primaryHover; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = palette.primary; }}
                            >
                                <DynamicIcon iconClass="fas fa-edit" /> Modifica
                            </button>
                        </div>
                    </div>
                    



                </div>
                
                {/* Colonna destra (2/3) - Tab per Commesse e Appuntamenti */}
                <div className="w-full xl:w-2/3">
                    <Folder 
                        tabTitleArray={tabTitles} 
                    >
                        {tabContents}
                    </Folder>
                </div>
            </div>
                        

            {/* Dialog di conferma eliminazione */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
                        <h3 className="text-xl font-bold mb-4 text-gray-800">Conferma Eliminazione</h3>
                        <p className="mb-6 text-gray-700">
                            Sei sicuro di voler eliminare la manutenzione "{selectedMaintenance.code || 'selezionata'}"?
                            Questa azione non può essere annullata.
                        </p>

                        {linkedJobs && linkedJobs.length > 0 && (
                            <div className="mb-6 p-3 bg-amber-50 border border-amber-300 rounded-md text-amber-800 text-sm">
                                <p className="font-semibold mb-1">Attenzione: commesse collegate</p>
                                <p className="mb-1">
                                    Questa manutenzione è collegata alle seguenti commesse. Eliminandola, i collegamenti andranno persi:
                                </p>
                                <p className="font-medium">{linkedJobs.map(j => j.code).join(', ')}</p>
                            </div>
                        )}

                        {deleteError && (
                            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md">
                                <p>{typeof deleteError === 'object' ? (deleteError.message || 'Errore durante l\'eliminazione') : deleteError}</p>
                            </div>
                        )}
                        
                        <div className="flex justify-end space-x-3">
                            <button 
                                onClick={() => { setShowDeleteConfirm(false); setLinkedJobs(null); }}
                                className="px-4 py-2 rounded font-medium transition-colors duration-150 ease-in-out"
                                style={{
                                    backgroundColor: '#f0f0f0',
                                    color: '#444444',
                                    border: '1px solid #dddddd'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#e0e0e0'; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                                disabled={deleteLoading}
                            >
                                Annulla
                            </button>
                            <button 
                                onClick={() => confirmDelete(!!(linkedJobs && linkedJobs.length > 0))}
                                className="px-4 py-2 rounded font-medium flex items-center justify-center gap-2 transition-colors duration-150 ease-in-out"
                                style={{
                                    backgroundColor: '#ffebee',
                                    color: '#d32f2f',
                                    border: '1px solid #ffcdd2'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#ffcdd2'; e.currentTarget.style.color = '#c62828'; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#ffebee'; e.currentTarget.style.color = '#d32f2f'; }}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? (
                                    <>
                                        <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                        Eliminazione in corso...
                                    </>
                                ) : (
                                    <>
                                        <DynamicIcon iconClass="fas fa-trash" />
                                        {linkedJobs && linkedJobs.length > 0 ? 'Elimina comunque' : 'Elimina'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
